import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation, FixedBottomButton } from '@/components'
import { TransferDetailsAvatar } from '@/components/TransferDetailsAvatar'
import { TransferDetailsBigAmount } from '@/components/TransferDetailsBigAmount'
import { TransferDetailsInfos } from '@/components/TransferDetailsInfos'
import { useGetSavingGoalTransferDetails } from '@/domain/transfer'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'

export interface SavingsTransferDetailParams
  extends Record<string, string | undefined> {
  goalId?: string
  transferId?: string
}

export const SavingsTransferDetailsScreen: React.FC = () => {
  const { goalId, transferId } = useParams<SavingsTransferDetailParams>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const offsets = useOffsets()

  const {
    data: transferDetails,
    isLoading: isLoadingTransferDetails,
    isError: isErrorGetTransferDetails,
    refetch,
  } = useGetSavingGoalTransferDetails({
    saving_goal_id: goalId || '',
    transfer_id: transferId || '',
  })

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    refetch()
  }

  const headerTitle = transferDetails?.displayTitle
    ? t(transferDetails.displayTitle)
    : t('translation:activityNotMapped')

  if (isErrorGetTransferDetails) {
    return (
      <BaseSafeFormView flex={1} bgColor="background.screen">
        {/* Fixed Header Navigation */}
        <StickyHeaderNavigation
          title={t('translation:error') || 'Error'}
          onBackPress={handleGoBack}
        />

        <BaseView flex={1} center px={16}>
          <BaseText fontSize={48} style={{ marginBottom: 16 }}>
            ❌
          </BaseText>
          <BaseText
            fontSize={18}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
            style={{ marginBottom: 8 }}
          >
            {t('translation:failedToLoadTransfer') || 'Failed to load transfer'}
          </BaseText>
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
            style={{ marginBottom: 24 }}
          >
            {t('translation:pleaseCheckConnectionAndTryAgain') ||
              'Please check your connection and try again'}
          </BaseText>
          <BaseView
            py={12}
            px={24}
            style={{
              backgroundColor: theme.colors.background.buttonPrimaryEnabled,
              borderRadius: '12px',
              cursor: 'pointer',
            }}
            onClick={handleRefresh}
          >
            <BaseText
              fontSize={16}
              fontFamily="medium"
              color="label.buttonPrimaryEnabled"
            >
              {t('translation:tryAgain') || 'Try Again'}
            </BaseText>
          </BaseView>
        </BaseView>
      </BaseSafeFormView>
    )
  }

  return (
    <BaseView>
      <BaseView
        style={{ paddingTop: offsets.top + 70 }}
        flex={1}
        bgColor="background.screen"
      >
        {/* Fixed Header Navigation */}
        <StickyHeaderNavigation
          title={headerTitle}
          onBackPress={handleGoBack}
        />

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            paddingBottom: '140px', // Space for fixed button
          }}
        >
          {/* Always render components with loading state - like in React Native */}
          <BaseView gap={40} align="center" pt={32} px={12}>
            {/* Transfer Details Avatar */}
            <TransferDetailsAvatar
              isLoading={isLoadingTransferDetails}
              {...(transferDetails || {})}
            />

            {/* Transfer Details Big Amount */}
            <TransferDetailsBigAmount
              isLoading={isLoadingTransferDetails}
              {...(transferDetails || {})}
            />

            {/* Transfer Details Infos */}
            <TransferDetailsInfos
              isLoading={isLoadingTransferDetails}
              {...(transferDetails || {})}
            />
          </BaseView>
        </div>
      </BaseView>

      {/* Fixed Bottom Button */}
      <FixedBottomButton
        buttons={{
          type: 'secondaryGray',
          label: t('translation:close') || 'Close',
          onPress: handleGoBack,
          isLoading: isLoadingTransferDetails,
        }}
      />
    </BaseView>
  )
}

export default SavingsTransferDetailsScreen
